/**
 * Dissolve Dialog
 *
 * Material-UI dialog for configuring dissolve operation parameters.
 * Allows selecting a dissolve field and configuring attribute aggregations.
 *
 * **Validates: Requirements 50.1, 50.5**
 */

import React, { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import type { AggregationOperation, FieldAggregation } from '../../services/GeometryProcessor';
import {
  setDissolveSourceLayer,
  setDissolveField,
  addDissolveAggregation,
  removeDissolveAggregation,
  closeTool,
} from '../../store/slices/geometryProcessingSlice';

interface DissolveDialogProps {
  open: boolean;
  onExecute: () => void;
}

const AGGREGATION_OPERATIONS: { value: AggregationOperation; label: string }[] = [
  { value: 'sum', label: 'Sum' },
  { value: 'mean', label: 'Mean' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'count', label: 'Count' },
];

export const DissolveDialog: React.FC<DissolveDialogProps> = ({ open, onExecute }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { dissolveTool, isProcessing, error, message } = useSelector(
    (state: RootState) => state.geometryProcessing
  );
  const layers = useSelector((state: RootState) => state.layers.layers);

  // Get available fields from the selected layer's features
  const availableFields = useMemo(() => {
    const selectedLayer = layers.find((l) => l.id === dissolveTool.sourceLayerId);
    if (!selectedLayer?.features || selectedLayer.features.length === 0) return [];

    const fieldSet = new Set<string>();
    for (const feature of selectedLayer.features) {
      if (feature.properties) {
        Object.keys(feature.properties).forEach((key) => {
          if (!key.startsWith('_')) fieldSet.add(key);
        });
      }
    }
    return Array.from(fieldSet);
  }, [layers, dissolveTool.sourceLayerId]);

  // Get numeric fields for aggregation
  const numericFields = useMemo(() => {
    const selectedLayer = layers.find((l) => l.id === dissolveTool.sourceLayerId);
    if (!selectedLayer?.features || selectedLayer.features.length === 0) return [];

    const numericSet = new Set<string>();
    for (const feature of selectedLayer.features) {
      if (feature.properties) {
        Object.entries(feature.properties).forEach(([key, value]) => {
          if (!key.startsWith('_') && typeof value === 'number') {
            numericSet.add(key);
          }
        });
      }
    }
    return Array.from(numericSet).filter((f) => f !== dissolveTool.dissolveField);
  }, [layers, dissolveTool.sourceLayerId, dissolveTool.dissolveField]);

  // State for adding new aggregation
  const [newAggField, setNewAggField] = React.useState<string>('');
  const [newAggOp, setNewAggOp] = React.useState<AggregationOperation>('sum');

  const handleClose = useCallback(() => {
    dispatch(closeTool());
  }, [dispatch]);

  const handleSourceLayerChange = useCallback(
    (e: any) => {
      dispatch(setDissolveSourceLayer(e.target.value));
    },
    [dispatch]
  );

  const handleDissolveFieldChange = useCallback(
    (e: any) => {
      dispatch(setDissolveField(e.target.value));
    },
    [dispatch]
  );

  const handleAddAggregation = useCallback(() => {
    if (newAggField) {
      dispatch(addDissolveAggregation({ field: newAggField, operation: newAggOp }));
      setNewAggField('');
    }
  }, [dispatch, newAggField, newAggOp]);

  const handleRemoveAggregation = useCallback(
    (field: string) => {
      dispatch(removeDissolveAggregation(field));
    },
    [dispatch]
  );

  const polygonLayers = useMemo(
    () => layers.filter((l) => l.geometryType === 'Polygon' || l.geometryType === 'MultiPolygon'),
    [layers]
  );

  const canExecute = dissolveTool.sourceLayerId && dissolveTool.dissolveField && !isProcessing;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dissolve Features</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Merge features with matching attribute values and remove internal boundaries.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="dissolve-source-layer-label">Source Layer</InputLabel>
            <Select
              labelId="dissolve-source-layer-label"
              value={dissolveTool.sourceLayerId || ''}
              onChange={handleSourceLayerChange}
              label="Source Layer"
            >
              {polygonLayers.map((layer) => (
                <MenuItem key={layer.id} value={layer.id}>
                  {layer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {dissolveTool.sourceLayerId && (
            <FormControl fullWidth>
              <InputLabel id="dissolve-field-label">Dissolve Field</InputLabel>
              <Select
                labelId="dissolve-field-label"
                value={dissolveTool.dissolveField || ''}
                onChange={handleDissolveFieldChange}
                label="Dissolve Field"
              >
                {availableFields.map((field) => (
                  <MenuItem key={field} value={field}>
                    {field}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {dissolveTool.dissolveField && numericFields.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Attribute Aggregation (optional)
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel id="agg-field-label">Field</InputLabel>
                  <Select
                    labelId="agg-field-label"
                    value={newAggField}
                    onChange={(e) => setNewAggField(e.target.value)}
                    label="Field"
                  >
                    {numericFields
                      .filter((f) => !dissolveTool.aggregations.find((a) => a.field === f))
                      .map((field) => (
                        <MenuItem key={field} value={field}>
                          {field}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel id="agg-op-label">Operation</InputLabel>
                  <Select
                    labelId="agg-op-label"
                    value={newAggOp}
                    onChange={(e) => setNewAggOp(e.target.value as AggregationOperation)}
                    label="Operation"
                  >
                    {AGGREGATION_OPERATIONS.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddAggregation}
                  disabled={!newAggField}
                >
                  Add
                </Button>
              </Box>

              {dissolveTool.aggregations.length > 0 && (
                <List dense>
                  {dissolveTool.aggregations.map((agg) => (
                    <ListItem key={agg.field}>
                      <ListItemText
                        primary={agg.field}
                        secondary={<Chip label={agg.operation} size="small" variant="outlined" />}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveAggregation(agg.field)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {message && !error && <Alert severity="success">{message}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={!canExecute}
          startIcon={isProcessing ? <CircularProgress size={16} /> : undefined}
        >
          {isProcessing ? 'Processing...' : 'Dissolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DissolveDialog;
